import { createBrowserClient } from "@supabase/ssr"
import { createClient } from "@supabase/supabase-js"
import { requestSessionRefresh, getTokenFetchUrl } from "@/lib/session-refresh-registry"

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL || "https://ycsnzmtqzgdcorvvxkfg.supabase.co"
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || ""

const RETRY_MARK = Symbol.for("supabase_401_retry")

/**
 * Custom fetch that intercepts 401, refreshes session/token, and retries once.
 * Retries both GET and POST requests (needed for .rpc() calls).
 */
function createSupabaseFetch(): typeof fetch {
    const rawFetch = globalThis.fetch.bind(globalThis)
    return async function supabaseFetch(
        input: RequestInfo | URL,
        init?: RequestInit
    ): Promise<Response> {
        // Clone init body so we can replay it on retry
        const bodyForRetry = init?.body ?? null
        const request = input instanceof Request ? input : new Request(input, init)
        const response = await rawFetch(request)

        if (
            typeof window !== "undefined" &&
            response.status === 401 &&
            !(request as Request & { [RETRY_MARK]?: boolean })[RETRY_MARK]
        ) {
            try {
                // Refresh the Supabase token directly. For B2C, also signal
                // SessionRefresh to renew the Stytch session (fire-and-forget —
                // the token endpoint handles expired Stytch JWTs via opaque session fallback).
                requestSessionRefresh().catch(() => {})
                const tokenRes = await rawFetch(getTokenFetchUrl(), {
                    credentials: "same-origin"
                })
                if (!tokenRes.ok) return response
                const { token } = await tokenRes.json()
                setSupabaseAccessToken(token)
                const newHeaders = new Headers(request.headers)
                newHeaders.set("Authorization", `Bearer ${token}`)
                const retryRequest = new Request(request.url, {
                    method: request.method,
                    headers: newHeaders,
                    body: bodyForRetry
                })
                ;(retryRequest as Request & { [RETRY_MARK]?: boolean })[RETRY_MARK] = true
                return rawFetch(retryRequest)
            } catch {
                return response
            }
        }
        return response
    }
}

/**
 * Server-side Supabase client using the service role key.
 * Bypasses RLS — use in API routes only.
 */
export function getServiceSupabase() {
    return createClient(SUPABASE_URL, process.env.SUPABASE_SERVICE_KEY!)
}

let supabaseClient: ReturnType<typeof createBrowserClient> | null = null
let currentAccessToken: string | null = null

/**
 * Get the current Supabase JWT access token (for passing to leads-server-2).
 */
export function getAccessToken(): string | null {
    return currentAccessToken
}

/**
 * Get a fresh access token, refreshing if the cached one is expired.
 * Falls back to the cached token if refresh fails.
 */
export async function getFreshAccessToken(): Promise<string | null> {
    if (currentAccessToken) {
        try {
            const payload = JSON.parse(atob(currentAccessToken.split(".")[1]))
            const now = Math.floor(Date.now() / 1000)
            if (payload.exp && payload.exp > now + 30) {
                return currentAccessToken
            }
        } catch {}
    }
    try {
        const res = await fetch(getTokenFetchUrl(), { credentials: "same-origin" })
        if (res.ok) {
            const { token } = await res.json()
            setSupabaseAccessToken(token)
            return token
        }
    } catch {}
    return currentAccessToken
}

const getClientOptions = (accessToken?: string) => {
    const base = { isSingleton: false }
    if (typeof window === "undefined") return base
    return {
        ...base,
        global: {
            fetch: createSupabaseFetch(),
            ...(accessToken && {
                headers: { Authorization: `Bearer ${accessToken}` }
            })
        }
    }
}

export function getSupabase() {
    if (!supabaseClient) {
        supabaseClient = createBrowserClient(
            SUPABASE_URL,
            SUPABASE_ANON_KEY,
            getClientOptions()
        )
    }
    return supabaseClient
}

/**
 * Recreate the Supabase client with a custom JWT in the Authorization header
 * so all subsequent queries carry the org_domain claim for RLS.
 */
let _setTokenCallCount = 0
export function setSupabaseAccessToken(accessToken: string) {
    _setTokenCallCount += 1
    if (typeof window !== "undefined") {
        const sameToken = currentAccessToken === accessToken
        console.log(`[diag] setSupabaseAccessToken call #${_setTokenCallCount}`, { sameToken, hadPriorClient: !!supabaseClient })
    }
    currentAccessToken = accessToken
    supabaseClient = createBrowserClient(
        SUPABASE_URL,
        SUPABASE_ANON_KEY,
        getClientOptions(accessToken)
    )
}
export async function saveSalesNavSearch(url: string, searchName: string, leadCount: number, userEmail?: string, domain?: string) {
    const supabase = getSupabase()

    const { data: orgData, error: fetchError } = await supabase
        .from("organizations")
        .select("sales_nav_lists")
        .eq("domain", domain)
        .single()

    if (fetchError) {
        console.error("[v0] Error fetching organization:", fetchError)
        return null
    }

    // sales_nav_lists is already JSONB[] - an array of objects
    const salesNavLists = orgData?.sales_nav_lists || []

    const newList = {
        id: crypto.randomUUID(),
        sales_nav_url: url,
        list_name: searchName,
        csvFilename: `sales-nav-search-${crypto.randomUUID()}.csv`,
        lead_count: leadCount,
        status: "in-progress",
        created_at: new Date().toISOString()
    }

    // Add as plain object - Supabase handles JSONB serialization
    salesNavLists.push(newList)

    const { data, error } = await supabase
        .from("organizations")
        .update({
            sales_nav_lists: salesNavLists
        })
        .eq("domain", domain)
        .select()
        .single()

    if (error) {
        console.error("[v0] Error updating Sales Nav lists:", error)
        return null
    }

    console.log("[v0] Saved Sales Nav search:", data)
    return salesNavLists
}
export async function saveLeadList(name: string, source: string, leadCount: number, searchId?: string, userEmail?: string) {
    const supabase = getSupabase()
    const { data, error } = await supabase
        .from("lead_lists")
        .insert({ name, source, lead_count: leadCount, search_id: searchId, user_email: userEmail })
        .select()
        .single()

    if (error) {
        console.error("[v0] Error saving lead list:", error)
        return null
    }
    console.log("[v0] Saved lead list:", data)
    return data
}

export async function saveLeads(listId: string | number, leads: unknown[]) {
    if (!listId) {
        console.error("[v0] Error: listId is required to save leads")
        return false
    }

    if (!leads || leads.length === 0) {
        console.log("[v0] No leads to save")
        return true
    }

    const supabase = getSupabase()
    const leadsData = leads.map(lead => ({
        list_id: listId,
        first_name: (lead as { first: string }).first,
        last_name: (lead as { last: string }).last,
        company: (lead as { company: string }).company,
        title: (lead as { title: string }).title,
        email: (lead as { email: string }).email,
        linkedin_url: (lead as { linkedin: string }).linkedin,
        verified: (lead as { verified: boolean }).verified || false
    }))

    const { error } = await supabase.from("leads").insert(leadsData)

    if (error) {
        console.error("[v0] Error saving leads:", error)
        return false
    }
    console.log("[v0] Saved", leadsData.length, "leads")
    return true
}

export async function saveEmailAccount(email: string, provider: string, userEmail?: string) {
    const supabase = getSupabase()
    const { data, error } = await supabase
        .from("email_accounts")
        .upsert(
            { email, provider, status: "active", daily_limit: 50, emails_remaining: 50, user_email: userEmail },
            { onConflict: "email" }
        )
        .select()
        .single()

    if (error) {
        console.error("[v0] Error saving email account:", error)
        return null
    }
    console.log("[v0] Saved email account:", data)
    return data
}

export async function saveLinkedInAccount(username: string, userEmail?: string) {
    const supabase = getSupabase()
    const { data, error } = await supabase
        .from("linkedin_accounts")
        .upsert({ username, status: "active", user_email: userEmail }, { onConflict: "username" })
        .select()
        .single()

    if (error) {
        console.error("[v0] Error saving LinkedIn account:", error)
        return null
    }
    console.log("[v0] Saved LinkedIn account:", data)
    return data
}

export async function saveUserEmail(email: string, userId?: string) {
    const supabase = getSupabase()
    const { data, error } = await supabase
        .from("users")
        .upsert(
            {
                email,
                user_id: userId,
                last_login: new Date().toISOString(),
                created_at: new Date().toISOString()
            },
            { onConflict: "email" }
        )
        .select()
        .single()

    if (error) {
        console.error("[v0] Error saving user email:", error)
        return null
    }
    console.log("[v0] Saved user email:", data)
    return data
}

export async function saveSequence(
    name: string,
    status: string,
    leadCount: number,
    sequenceData: unknown,
    selectedLists: (string | number)[],
    userEmail?: string
) {
    const supabase = getSupabase()

    console.log("[v0] Saving sequence:", { name, status, leadCount })

    const { data: sequence, error: seqError } = await supabase
        .from("sequences")
        .insert({ name, status, lead_count: leadCount, config: sequenceData, is_saved: true, user_email: userEmail })
        .select()
        .single()

    if (seqError) {
        console.error("[v0] Error saving sequence:", seqError)
        return null
    }

    console.log("[v0] Saved sequence:", sequence)

    if (selectedLists.length > 0 && sequence) {
        const listLinks = selectedLists.map(listId => ({
            sequence_id: sequence.id,
            list_id: listId
        }))

        const { error: linkError } = await supabase.from("sequence_lead_lists").insert(listLinks)

        if (linkError) {
            console.error("[v0] Error linking lead lists:", linkError)
        } else {
            console.log("[v0] Linked", listLinks.length, "lead lists to sequence")
        }
    }

    return sequence
}

export async function getAllOrganizations() {
    try {
        const res = await fetch("/api/admin/organizations", { credentials: "same-origin" })
        if (!res.ok) {
            console.error("[v0] Error fetching all organizations:", res.status)
            return []
        }
        return await res.json()
    } catch (err) {
        console.error("[v0] Error fetching all organizations:", err)
        return []
    }
}
